// Dark-mode page background for the Home Hub (used by Hub.jsx when the user's Home Page
// Background preference is 'dark'). Two fixed layers, pinned to the viewport so they never
// scroll with the tiles:
//   1. graphite gradient, lighter at the top left and darker at the bottom right
//   2. contour waves: clear space under the header first, then a wide gap between the lines at
//      the top that tightens progressively as the waves cascade down the page
// The fine dot grid is no longer part of this background — it now belongs to the separate
// Global Header background (see AppHeader.jsx).

const BASE_GRADIENT = 'linear-gradient(165deg, #474a53 0%, #2a2c33 50%, #121317 100%)';

// Contour waves are one tall tile that repeats sideways only, so the spacing between the lines can
// change down the page (a plain repeating tile can only ever have even spacing). Tune the look here:
//   WAVE_START_Y   where the first line sits (leaves clear graphite under the header before the waves begin)
//   WAVE_FIRST_GAP gap between the first two lines (the widest gap, at the top)
//   WAVE_GAP_RATIO each following gap is this fraction of the one before it (lower = tightens faster)
//   WAVE_MIN_GAP   the tightest gap the lines ever reach lower down the page
const WAVE_TILE_W = 240;
const WAVE_TILE_H = 1600;
const WAVE_START_Y = 230;
const WAVE_FIRST_GAP = 64;
const WAVE_GAP_RATIO = 0.93;
const WAVE_MIN_GAP = 9;

const buildWaves = () => {
  const paths = [];
  let y = WAVE_START_Y;
  let gap = WAVE_FIRST_GAP;
  while (y < WAVE_TILE_H) {
    // Bigger, lazier waves where the lines are far apart; gentler ripples where they are close.
    const amp = Math.max(2, Math.min(13, gap * 0.32));
    paths.push(`<path d='M0 ${y.toFixed(1)}Q60 ${(y - amp).toFixed(1)} 120 ${y.toFixed(1)}T${WAVE_TILE_W} ${y.toFixed(1)}'/>`);
    y += gap;
    gap = Math.max(WAVE_MIN_GAP, gap * WAVE_GAP_RATIO);
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${WAVE_TILE_W}' height='${WAVE_TILE_H}'><g fill='none' stroke='white' stroke-opacity='0.10'>${paths.join('')}</g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const WAVES = buildWaves();

// Soft fade-in over the first couple of lines so the waves ease in rather than start abruptly.
const WAVES_MASK = 'linear-gradient(to bottom, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 200px, #000 330px, #000 100%)';

const HubDarkBackground = () => (
  <>
    <div
      className="fixed inset-0 pointer-events-none"
      style={{ background: BASE_GRADIENT }}
      data-testid="hub-fixed-dark-background"
    />
    <div
      className="fixed inset-0 pointer-events-none"
      style={{
        backgroundImage: WAVES,
        backgroundSize: `${WAVE_TILE_W}px ${WAVE_TILE_H}px`,
        backgroundRepeat: 'repeat-x',
        backgroundPosition: '0 0',
        WebkitMaskImage: WAVES_MASK,
        maskImage: WAVES_MASK,
      }}
      data-testid="hub-fixed-dark-background-waves"
    />
  </>
);

export default HubDarkBackground;
